import type { Node as ProseMirrorNode } from '@tiptap/pm/model';
import type { EditorState, Transaction } from '@tiptap/pm/state';
import { signatureSlotKey } from '@dealstack/shared/lib/common/business/signatureSlotKey';

import { SIGNATURE_NODE_NAME, signatureNodeSlotKey } from './signatureNode';
import type { SignatureSlotPayload } from './signatureDrop';

/** A signature anchor in the document, and where it sits. */
export interface FoundSignature {
	pos: number;
	node: ProseMirrorNode;
	slotKey: string;
}

/**
 * Every signature anchor in the document, in document order.
 *
 * `descendants` rather than a walk over the flow model: this runs against the LIVE editor state,
 * which is the only thing that knows about an anchor typed a keystroke ago. The flow-model
 * walker (`flowSignatureSlots`) answers the same question for a saved document.
 */
export const findSignatures = (doc: ProseMirrorNode): FoundSignature[] => {
	const found: FoundSignature[] = [];
	doc.descendants((node, pos) => {
		if (node.type.name !== SIGNATURE_NODE_NAME) {
			return true;
		}
		found.push({ pos, node, slotKey: signatureNodeSlotKey(node.attrs) });
		// An atom has no children worth descending into.
		return false;
	});
	return found;
};

const CELL_TYPES = new Set(['tableCell', 'tableHeader']);

const holdsSignature = (node: ProseMirrorNode): boolean => {
	let found = false;
	node.descendants((child) => {
		if (child.type.name === SIGNATURE_NODE_NAME) {
			found = true;
		}
		return !found;
	});
	return found;
};

/**
 * The next cell of the same row, when it holds no anchor of its own. Placing a pair is two
 * clicks at ONE caret and an insert does not move it, so both anchors stacked in the first cell
 * while the palette reported both slots placed. Forward only, and never into an occupied cell.
 */
export const caretAfterSignatureInsert = (doc: ProseMirrorNode, pos: number): number | null => {
	const $pos = doc.resolve(pos);
	for (let depth = $pos.depth; depth > 0; depth -= 1) {
		if (!CELL_TYPES.has($pos.node(depth).type.name)) {
			continue;
		}
		const row = $pos.node(depth - 1);
		const next = row.maybeChild($pos.index(depth - 1) + 1);
		if (next === null || next === undefined || holdsSignature(next)) {
			return null;
		}
		// Just inside the next cell; the caller resolves it to the nearest text position.
		return $pos.after(depth) + 1;
	}
	return null;
};

/** The slot keys the document currently holds, for the palette's placed/unplaced state. */
export const placedSignatureKeys = (doc: ProseMirrorNode): Set<string> =>
	new Set(findSignatures(doc).map((entry) => entry.slotKey));

/** The first anchor for a slot, or null. */
export const findSignatureForSlot = (
	doc: ProseMirrorNode,
	slot: SignatureSlotPayload
): FoundSignature | null => {
	const wanted = signatureSlotKey(slot);
	return findSignatures(doc).find((entry) => entry.slotKey === wanted) ?? null;
};

/**
 * A transaction deleting every anchor for a slot, or null when it holds none.
 *
 * EVERY one, not the first: a document that somehow holds two anchors for one slot cannot be
 * saved, and a Remove that cleared only one would leave the author pressing it again with no
 * sign that anything had happened. Deleted back-to-front so the earlier positions stay valid.
 */
export const removeSignatureSlot = (
	state: EditorState,
	slot: SignatureSlotPayload
): Transaction | null => {
	const wanted = signatureSlotKey(slot);
	const matches = findSignatures(state.doc).filter((entry) => entry.slotKey === wanted);
	if (matches.length === 0) {
		return null;
	}
	const transaction = state.tr;
	for (const entry of [...matches].reverse()) {
		transaction.delete(entry.pos, entry.pos + entry.node.nodeSize);
	}
	return transaction;
};
